import React, { useState } from 'react';
import { X, Edit2, Camera, Grid, List } from 'lucide-react';

const AMENIDADES_OPTIONS = [
  'alberca',
  'jardin',
  'terraza',
  'cocina_equipada',
  'sala',
  'comedor',
  'recamara_principal',
  'recamara_secundaria',
  'baño',
  'medio_baño',
  'estacionamiento',
  'cochera',
  'garaje',
  'patio',
  'cuarto_servicio',
  'lavanderia',
  'bodega',
  'estudio',
  'biblioteca',
  'gimnasio',
  'sala_cine',
  'bar',
  'chimenea',
  'jacuzzi',
  'sauna',
  'vista_panoramica',
  'vista_ciudad',
  'vista_jardin',
  'balcon',
  'roof_garden',
  'terraza_privada',
  'piscina',
  'asador',
  'area_verde',
];

const buildFileKey = (file) => [file.name, file.size, file.lastModified].join('-');

const ImageTagEditor = ({ images = [], onImagesChange }) => {
  const [viewMode, setViewMode] = useState('grid'); // 'grid' o 'list'
  const [editingIndex, setEditingIndex] = useState(null);
  const [selectedAmenidades, setSelectedAmenidades] = useState([]);
  const [descripcion, setDescripcion] = useState('');

  const handleAddImage = (files) => {
    const existingKeys = new Set(
      images.filter((image) => image.file).map((image) => buildFileKey(image.file))
    );

    const newImages = [];

    for (const file of Array.from(files)) {
      const fileKey = buildFileKey(file);

      if (existingKeys.has(fileKey)) {
        continue;
      }

      existingKeys.add(fileKey);
      newImages.push({
        file,
        url: URL.createObjectURL(file),
        descripcion: '',
        tags: [],
        orden: images.length + newImages.length,
      });

      if (images.length + newImages.length >= 30) {
        break;
      }
    }

    onImagesChange([...images, ...newImages]);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    onImagesChange(newImages);
  };

  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setSelectedAmenidades([...images[index].tags]);
    setDescripcion(images[index].descripcion);
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null) {
      const newImages = [...images];
      newImages[editingIndex] = {
        ...newImages[editingIndex],
        descripcion,
        tags: selectedAmenidades,
      };
      onImagesChange(newImages);
      setEditingIndex(null);
      setSelectedAmenidades([]);
      setDescripcion('');
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setSelectedAmenidades([]);
    setDescripcion('');
  };

  const handleReorder = (dragIndex, dropIndex) => {
    const newImages = [...images];
    const [dragged] = newImages.splice(dragIndex, 1);
    newImages.splice(dropIndex, 0, dragged);

    // Actualizar orden
    newImages.forEach((img, index) => {
      img.orden = index;
    });

    onImagesChange(newImages);
  };

  const toggleAmenidad = (amenidad) => {
    if (selectedAmenidades.includes(amenidad)) {
      setSelectedAmenidades(selectedAmenidades.filter((a) => a !== amenidad));
    } else {
      setSelectedAmenidades([...selectedAmenidades, amenidad]);
    }
  };

  const formatAmenidad = (text) => {
    return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Galería de Imágenes</h3>
          <p className="text-sm text-slate-500">
            {images.length}/30 imágenes. Arrastra para reordenar, haz click para etiquetar.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setViewMode('grid')}
            className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}
          >
            <Grid size={18} />
          </button>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}`}
          >
            <List size={18} />
          </button>
        </div>
      </div>

      {/* Upload area */}
      <div className="border-2 border-dashed border-slate-300 rounded-2xl p-8 text-center hover:border-blue-400 transition-colors bg-slate-50">
        <input
          type="file"
          multiple
          onChange={(e) => handleAddImage(e.target.files)}
          accept="image/*"
          className="hidden"
          id="image-upload"
          disabled={images.length >= 30}
        />
        <label
          htmlFor="image-upload"
          className={`cursor-pointer flex flex-col items-center gap-3 ${images.length >= 30 ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Camera className="text-slate-400" size={32} />
          <div>
            <p className="text-sm font-medium text-slate-700">
              Arrastra imágenes o haz click para subir
            </p>
            <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF, WEBP (máx. 10MB cada una)</p>
            <p className="text-xs text-slate-500">{images.length}/30 imágenes</p>
          </div>
        </label>
      </div>

      {/* Editing Modal */}
      {editingIndex !== null && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-slate-800">
                  Etiquetar Imagen {editingIndex + 1}
                </h3>
                <button onClick={handleCancelEdit} className="p-2 hover:bg-slate-100 rounded-lg">
                  <X size={20} />
                </button>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Imagen Preview */}
                <div>
                  <div className="text-sm font-medium text-slate-700 mb-2">Vista Previa</div>
                  <div className="border border-slate-200 rounded-xl overflow-hidden">
                    <img
                      src={images[editingIndex].url}
                      alt="Editando"
                      className="w-full h-64 object-cover"
                    />
                  </div>
                </div>

                {/* Formulario */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Descripción de la imagen
                    </label>
                    <textarea
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      placeholder="¿Qué se ve en esta imagen?"
                      className="w-full h-32 border border-slate-300 rounded-lg p-3 text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Amenidades visibles
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {selectedAmenidades.map((amenidad) => (
                        <span
                          key={amenidad}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm"
                        >
                          {formatAmenidad(amenidad)}
                          <button
                            type="button"
                            onClick={() => toggleAmenidad(amenidad)}
                            className="hover:text-blue-900"
                          >
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto p-2 border border-slate-200 rounded-lg">
                      {AMENIDADES_OPTIONS.map((amenidad) => (
                        <label
                          key={amenidad}
                          className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedAmenidades.includes(amenidad)}
                            onChange={() => toggleAmenidad(amenidad)}
                            className="rounded text-blue-600"
                          />
                          <span className="text-sm text-slate-700">{formatAmenidad(amenidad)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-3 pt-4 border-t border-slate-200">
                    <button
                      type="button"
                      onClick={handleSaveEdit}
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700"
                    >
                      Guardar Etiquetas
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="flex-1 border border-slate-300 text-slate-700 py-3 rounded-lg font-medium hover:bg-slate-50"
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Gallery */}
      {images.length > 0 && (
        <div
          className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-4 gap-4' : 'space-y-4'}
        >
          {images.map((image, index) => (
            <div
              key={index}
              className={`group relative border border-slate-200 rounded-xl overflow-hidden ${
                viewMode === 'list' ? 'flex gap-4' : ''
              }`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/plain', index);
              }}
              onDragOver={(e) => {
                e.preventDefault();
              }}
              onDrop={(e) => {
                e.preventDefault();
                const dragIndex = parseInt(e.dataTransfer.getData('text/plain'));
                if (dragIndex !== index) {
                  handleReorder(dragIndex, index);
                }
              }}
            >
              {/* Imagen */}
              <div
                className={`${viewMode === 'list' ? 'w-1/3' : 'aspect-square'} overflow-hidden bg-slate-100`}
              >
                <img
                  src={image.url}
                  alt={`Imagen ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>

              {/* Info & Actions */}
              <div
                className={`${viewMode === 'list' ? 'w-2/3' : 'absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent'} p-3`}
              >
                <div className="flex justify-between items-start">
                  <div className={`${viewMode === 'list' ? 'text-slate-700' : 'text-white'}`}>
                    <div className="text-xs font-bold">
                      Imagen {index + 1}
                      {image.descripcion && viewMode === 'list' && (
                        <div className="text-sm font-normal mt-1 line-clamp-2">
                          {image.descripcion}
                        </div>
                      )}
                    </div>
                    {image.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {image.tags.slice(0, 2).map((tag, i) => (
                          <span
                            key={i}
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              viewMode === 'list'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-white/90 text-slate-800'
                            }`}
                          >
                            {formatAmenidad(tag)}
                          </span>
                        ))}
                        {image.tags.length > 2 && (
                          <span
                            className={`text-xs px-2 py-0.5 rounded-full ${
                              viewMode === 'list'
                                ? 'bg-slate-100 text-slate-600'
                                : 'bg-white/90 text-slate-800'
                            }`}
                          >
                            +{image.tags.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleStartEdit(index)}
                      className={`p-1.5 rounded-lg ${
                        viewMode === 'list'
                          ? 'bg-slate-100 hover:bg-slate-200 text-slate-600'
                          : 'bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm'
                      }`}
                      title="Etiquetar imagen"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(index)}
                      className={`p-1.5 rounded-lg ${
                        viewMode === 'list'
                          ? 'bg-red-50 hover:bg-red-100 text-red-600'
                          : 'bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm'
                      }`}
                      title="Eliminar imagen"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>

              {/* Drag handle */}
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="w-6 h-6 bg-black/50 text-white rounded flex items-center justify-center text-xs cursor-move">
                  ⋮⋮
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary */}
      {images.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-slate-700">Resumen de etiquetas</div>
              <div className="text-xs text-slate-500 mt-1">
                {images.filter((img) => img.tags.length > 0).length} imágenes etiquetadas
              </div>
            </div>
            <div className="flex items-center gap-2">
              {Array.from(new Set(images.flatMap((img) => img.tags)))
                .slice(0, 3)
                .map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full"
                  >
                    {formatAmenidad(tag)}
                  </span>
                ))}
              {Array.from(new Set(images.flatMap((img) => img.tags))).length > 3 && (
                <span className="text-xs text-slate-500">
                  +{Array.from(new Set(images.flatMap((img) => img.tags))).length - 3} más
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageTagEditor;
